import { Outlet, useLocation } from "react-router-dom"
import { createContext, useContext, useState } from "react"
import ReactGA from "react-ga4"
import ReactPixel from "react-facebook-pixel"
import ScrollToTop from "./ScrollToTop"
import { AlertContext } from "./dashboard/contexts"
import { UseAlertStore } from "./dashboard/GlobalAlert"
import { useEffect } from "react"
import { NATIVE_DEVICE_ID, settings } from "@/settings"
import { AppTrackingTransparency } from "capacitor-plugin-app-tracking-transparency"

// Create a global user context for tracking
export const GlobalUserContext = createContext<{
  email: string | null
  setEmail: (email: string | null) => void
  user_id: string | null
  setUserId: (user_id: string | null) => void
}>({
  email: null,
  setEmail: () => {},
  user_id: null,
  setUserId: () => {}
})

export default function GlobalLayout() {
  const { alerts, addAlert, removeAlert } = UseAlertStore()
  const location = useLocation()
  const [userEmail, setUserEmail] = useState<string | null>(null)
  const [userId, setUserId] = useState<string | null>(null)
  const [iosAppTrackingEnabled, setIosAppTrackingEnabled] = useState(false)

  useEffect(() => {
    console.log("Page location changed to:", location.pathname)
  }, [location])

  return (
    <IosTrackingContext.Provider value={{ iosAppTrackingEnabled, setIosAppTrackingEnabled }}>
      <GlobalUserContext.Provider
        value={{ email: userEmail, setEmail: setUserEmail, user_id: userId, setUserId: setUserId }}
      >
        <AlertContext.Provider value={{ alerts, addAlert, removeAlert }}>
          <ScrollToTop />
          <TrackingComponent />
          <Outlet />
        </AlertContext.Provider>
      </GlobalUserContext.Provider>
    </IosTrackingContext.Provider>
  )
}

export const IosTrackingContext = createContext<{
  iosAppTrackingEnabled: boolean
  setIosAppTrackingEnabled: (enabled: boolean) => void
}>({
  iosAppTrackingEnabled: false,
  setIosAppTrackingEnabled: () => {}
})

function TrackingComponent() {
  const location = useLocation()
  const { email, user_id } = useContext(GlobalUserContext)
  const { iosAppTrackingEnabled, setIosAppTrackingEnabled } = useContext(IosTrackingContext)

  useEffect(() => {
    if (settings.IS_IOS) {
      AppTrackingTransparency.requestPermission().then((response) => {
        console.log("AppTrackingTransparency response:", response)

        if (response.status === "authorized") {
          setIosAppTrackingEnabled(true)
        }
      })
    }
  }, [])

  useEffect(() => {
    if (settings.IS_IOS && !iosAppTrackingEnabled) {
      return
    }

    ReactGA.initialize(settings.GA_MEASUREMENT_ID)

    if (email) {
      const advancedMatching = { em: email } // optional, more info: https://developers.facebook.com/docs/facebook-pixel/advanced/advanced-matching
      const options = {
        autoConfig: true, // set pixel's autoConfig. More info: https://developers.facebook.com/docs/facebook-pixel/advanced/
        debug: true
      }
      // @ts-ignore
      ReactPixel.init(settings.FACEBOOK_PIXEL_ID, advancedMatching, options)
    } else {
      ReactPixel.init(settings.FACEBOOK_PIXEL_ID)
    }
  }, [email, iosAppTrackingEnabled]) // Re-initialize when email changes

  useEffect(() => {
    if (settings.IS_IOS && !iosAppTrackingEnabled) {
      return
    }

    ReactPixel.pageView()

    ReactGA.gtag("config", settings.GA_MEASUREMENT_ID, {
      email: email,
      user_id: user_id,
      device_id: NATIVE_DEVICE_ID,
      platform: settings.PLATFORM
    })

    console.log("Sending pageview to GA", location.pathname)
    ReactGA.send({ hitType: "pageview", page: location.pathname, title: document.title })
  }, [location, iosAppTrackingEnabled])

  return null
}
